import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../lib/api';
import {
  ChevronRight, ChevronLeft, ZoomIn, ZoomOut, Maximize, Minimize,
  ArrowRight, Sun, Moon, Loader2, Bookmark, BookmarkCheck,
  MessageCircleQuestion, X, Send, HelpCircle, CheckCircle,
  Eye, EyeOff, User, FileText
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const ZOOM_LEVELS = [0.5, 0.75, 1, 1.25, 1.5, 2, 2.5];

const BookReaderPage = () => {
  const { courseId, bookKey } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = localStorage.getItem('token');

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [zoomIndex, setZoomIndex] = useState(2);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [nightMode, setNightMode] = useState(false);
  const [loading, setLoading] = useState(true);
  const [pageLoading, setPageLoading] = useState(false);
  const [bookTitle, setBookTitle] = useState('');
  const [pageInputValue, setPageInputValue] = useState('');

  const [bookmarks, setBookmarks] = useState([]);
  const [showBookmarks, setShowBookmarks] = useState(false);

  // Reading report from book reader
  const [showReportModal, setShowReportModal] = useState(false);
  const [submittingReport, setSubmittingReport] = useState(false);
  const [bookAuthor, setBookAuthor] = useState('');
  const [lastSubmittedPage, setLastSubmittedPage] = useState(0);

  // Click-to-ask approach
  const [clickAnchor, setClickAnchor] = useState(null); // {nx, ny, px, py}
  const [showAskModal, setShowAskModal] = useState(false);
  const [questionText, setQuestionText] = useState('');
  const [submittingQ, setSubmittingQ] = useState(false);
  const [pageQuestions, setPageQuestions] = useState([]);
  const [myPageQuestions, setMyPageQuestions] = useState([]);
  const [activeQuestion, setActiveQuestion] = useState(null);
  const [showQuestionsPanel, setShowQuestionsPanel] = useState(false);
  const [questionVisibility, setQuestionVisibility] = useState('all');

  const containerRef = useRef(null);
  const wrapperRef = useRef(null);
  const pageContainerRef = useRef(null);
  const touchStartRef = useRef(null);
  const longPressTimer = useRef(null);

  const scale = ZOOM_LEVELS[zoomIndex];
  const bmKey = `bm_${courseId}_${bookKey}`;

  // Load book info
  useEffect(() => {
    const load = async () => {
      try {
        const [progressRes, infoRes] = await Promise.all([
          api.get(`/courses/${courseId}/progress`),
          api.get(`/books/${bookKey}/info`),
        ]);
        const book = progressRes.data.find(b => b.book_key === bookKey);
        if (book) {
          setBookTitle(book.title);
          setBookAuthor(book.author || '');
          setLastSubmittedPage(book.pages_read || 0);
        }
        setTotalPages(infoRes.data.page_count);
        const urlPage = parseInt(searchParams.get('page'), 10);
        if (urlPage && urlPage >= 1 && urlPage <= infoRes.data.page_count) setCurrentPage(urlPage);
        else if (book && book.pages_read > 0) setCurrentPage(Math.min(book.pages_read, book.effective_total_pages));
        try { setBookmarks(JSON.parse(localStorage.getItem(bmKey) || '[]')); } catch (e) { console.error('Failed to parse bookmarks:', e); }
      } catch { toast.error('فشل تحميل الكتاب'); setTimeout(() => navigate(`/courses/${courseId}`), 2000); }
      finally { setLoading(false); }
    };
    load();
  }, [courseId, bookKey]); // eslint-disable-line

  // Load questions for current page
  useEffect(() => {
    if (loading || !totalPages) return;
    const loadPageData = async () => {
      try {
        const [qRes, myQRes] = await Promise.all([
          api.get(`/books/${bookKey}/questions?page_num=${currentPage}`),
          api.get(`/books/${bookKey}/my-questions`),
        ]);
        setPageQuestions(qRes.data || []);
        setMyPageQuestions((myQRes.data || []).filter(q => q.page_num === currentPage));
      } catch { setPageQuestions([]); setMyPageQuestions([]); }
    };
    loadPageData();
  }, [currentPage, bookKey, loading, totalPages]); // eslint-disable-line

  const pageImageUrl = useCallback((pageNum) => {
    const dpr = window.devicePixelRatio || 2;
    return `${API}/api/books/${bookKey}/page/${pageNum}?dpr=${Math.min(dpr, 3)}&token=${token}`;
  }, [bookKey, token]);

  const goToPage = useCallback((pageNum) => {
    const p = Math.max(1, Math.min(pageNum, totalPages));
    setCurrentPage(p);
    setPageInputValue('');
    setActiveQuestion(null);
    setClickAnchor(null);
    if (containerRef.current) containerRef.current.scrollTop = 0;
  }, [totalPages]);
  const nextPage = useCallback(() => goToPage(currentPage + 1), [currentPage, goToPage]);
  const prevPage = useCallback(() => goToPage(currentPage - 1), [currentPage, goToPage]);

  const zoomIn = useCallback(() => setZoomIndex(i => Math.min(i + 1, ZOOM_LEVELS.length - 1)), []);
  const zoomOut = useCallback(() => setZoomIndex(i => Math.max(i - 1, 0)), []);
  const resetZoom = useCallback(() => setZoomIndex(2), []);

  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) wrapperRef.current?.requestFullscreen?.();
    else document.exitFullscreen?.();
  }, []);
  useEffect(() => { const h = () => setIsFullscreen(!!document.fullscreenElement); document.addEventListener('fullscreenchange', h); return () => document.removeEventListener('fullscreenchange', h); }, []);

  // Keyboard
  useEffect(() => {
    const handler = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      if (showAskModal || activeQuestion || showBookmarks || showQuestionsPanel) {
        if (e.key === 'Escape') { setShowAskModal(false); setActiveQuestion(null); setShowBookmarks(false); setShowQuestionsPanel(false); }
        return;
      }
      switch (e.key) {
        case 'ArrowLeft': nextPage(); e.preventDefault(); break;
        case 'ArrowRight': prevPage(); e.preventDefault(); break;
        case '+': case '=': zoomIn(); e.preventDefault(); break;
        case '-': zoomOut(); e.preventDefault(); break;
        case 'f': case 'F': toggleFullscreen(); e.preventDefault(); break;
        case 'Escape': if (!document.fullscreenElement) navigate(`/courses/${courseId}`); break;
        default: break;
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [nextPage, prevPage, zoomIn, zoomOut, toggleFullscreen, navigate, courseId, showAskModal, activeQuestion, showBookmarks, showQuestionsPanel]); // eslint-disable-line

  // Swipe
  const handleTouchStart = useCallback((e) => { touchStartRef.current = { x: e.touches[0].clientX, y: e.touches[0].clientY }; }, []);
  const handleTouchEnd = useCallback((e) => {
    if (!touchStartRef.current) return;
    const dx = e.changedTouches[0].clientX - touchStartRef.current.x;
    const dy = e.changedTouches[0].clientY - touchStartRef.current.y;
    if (Math.abs(dx) > Math.abs(dy) && Math.abs(dx) > 60) { dx > 0 ? prevPage() : nextPage(); }
    touchStartRef.current = null;
  }, [nextPage, prevPage]);

  useEffect(() => { const h = e => e.preventDefault(); document.addEventListener('contextmenu', h); return () => document.removeEventListener('contextmenu', h); }, []);

  // Click on page to place question anchor
  const handlePageClick = useCallback((e) => {
    if (!pageContainerRef.current) return;
    const rect = pageContainerRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const nx = x / rect.width;
    const ny = y / rect.height;
    setClickAnchor({ nx, ny, px: x, py: y });
  }, []);

  // Long press on mobile
  const handlePageTouchStart = useCallback((e) => {
    const touch = e.touches[0];
    longPressTimer.current = setTimeout(() => {
      if (!pageContainerRef.current) return;
      const rect = pageContainerRef.current.getBoundingClientRect();
      const x = touch.clientX - rect.left;
      const y = touch.clientY - rect.top;
      setClickAnchor({ nx: x / rect.width, ny: y / rect.height, px: x, py: y });
    }, 500);
  }, []);
  const handlePageTouchEnd = useCallback(() => { clearTimeout(longPressTimer.current); }, []);

  const handlePageInput = (e) => { if (e.key === 'Enter') { const n = parseInt(pageInputValue, 10); if (!isNaN(n)) goToPage(n); } };

  const isBookmarked = bookmarks.includes(currentPage);
  const toggleBookmark = useCallback(() => {
    setBookmarks(prev => {
      const next = prev.includes(currentPage) ? prev.filter(p => p !== currentPage) : [...prev, currentPage].sort((a, b) => a - b);
      localStorage.setItem(bmKey, JSON.stringify(next));
      toast.success(prev.includes(currentPage) ? 'تم إزالة الإشارة المرجعية' : `تم حفظ إشارة - صفحة ${currentPage}`);
      return next;
    });
  }, [currentPage, bmKey]);

  // Submit reading report from book reader
  const reportFromPage = Math.min((lastSubmittedPage || 0) + 1, currentPage);
  const reportPagesCount = Math.max(0, currentPage - (reportFromPage - 1));
  // Deep-link from a course question — user opened the book at a specific page
  // to review a question, not to record reading progress. Hide the report button
  // to prevent falsely counting large page jumps as "read".
  const fromQuestion = !!searchParams.get('page');
  const submitReadingReport = async () => {
    if (reportPagesCount <= 0) { toast.error('لا توجد صفحات جديدة لإرسالها'); return; }
    setSubmittingReport(true);
    try {
      const isCompleted = currentPage >= totalPages ? 'نعم' : 'لا';
      const submitRes = await api.post('/reports/submit', {
        classification: '⛓️\u200dالمساقات⛓️',
        title: bookTitle,
        author: bookAuthor || '',
        pages_read: reportPagesCount,
        total_pages: totalPages,
        is_completed: isCompleted,
        course_id: courseId,
        book_key: bookKey,
        from_page: reportFromPage,
        to_page: currentPage,
      });
      const respData = submitRes?.data || {};
      setLastSubmittedPage(currentPage);
      setShowReportModal(false);
      // Stash celebration intent so Layout shows the celebration card + the
      // lavish course-completion modal (when applicable) after navigation.
      try {
        localStorage.setItem('pending_celebration', JSON.stringify({
          is_completed: isCompleted,
          title: bookTitle,
          author: bookAuthor || '',
          total_pages: totalPages,
          classification: '⛓️\u200dالمساقات⛓️',
          book_completed: !!respData.book_completed,
          course_completed: !!respData.course_completed,
          completed_course_title: respData.completed_course_title || '',
          completed_book_title: respData.completed_book_title || '',
        }));
      } catch {}
      // Navigate back to course page so Layout mounts and triggers celebration
      navigate(`/courses/${courseId}`);
    } catch (err) { toast.error(err.response?.data?.detail || 'فشل إرسال التقرير'); }
    finally { setSubmittingReport(false); }
  };

  const submitQuestion = async () => {
    if (!questionText.trim()) { toast.error('يرجى كتابة السؤال'); return; }
    setSubmittingQ(true);
    try {
      await api.post(`/books/${bookKey}/questions`, {
        page_num: currentPage,
        selected_text: '',
        question: questionText.trim(),
        text_anchor: clickAnchor ? { nx: clickAnchor.nx, ny: clickAnchor.ny } : {},
      });
      toast.success('تم إرسال سؤالك بنجاح وسيتم مراجعته');
      setShowAskModal(false);
      setQuestionText('');
      setClickAnchor(null);
      const myQRes = await api.get(`/books/${bookKey}/my-questions`);
      setMyPageQuestions((myQRes.data || []).filter(q => q.page_num === currentPage));
    } catch { toast.error('فشل إرسال السؤال'); }
    finally { setSubmittingQ(false); }
  };

  useEffect(() => {
    if (!totalPages || !token) return;
    [currentPage + 1, currentPage - 1].forEach(n => { if (n >= 1 && n <= totalPages) { const img = new Image(); img.src = pageImageUrl(n); } });
  }, [currentPage, totalPages, token, pageImageUrl]);

  const progressPercent = totalPages > 0 ? ((currentPage / totalPages) * 100) : 0;
  const visibleQuestions = questionVisibility === 'hidden' ? [] : questionVisibility === 'mine' ? myPageQuestions : pageQuestions;
  const questionsCount = pageQuestions.length + myPageQuestions.filter(q => q.status !== 'answered').length;
  const btnClass = (active) => active ? 'text-amber-500' : nightMode ? 'hover:bg-white/10 text-amber-300' : 'hover:bg-black/5 text-amber-800';

  if (loading) {
    return (
      <div className="fixed inset-0 z-[200] flex items-center justify-center" style={{ background: '#1a1a2e' }} dir="rtl">
        <div className="text-center space-y-6 px-8">
          <Loader2 className="w-20 h-20 text-amber-400 animate-spin mx-auto" />
          <p className="text-amber-100 text-xl font-bold">{bookTitle || 'جارٍ تحميل الكتاب...'}</p>
        </div>
      </div>
    );
  }

  return (
    <div ref={wrapperRef} className="fixed inset-0 z-[200] flex flex-col select-none"
      style={{ background: nightMode ? '#0f0f17' : '#f5f0e8', userSelect: 'none', WebkitUserSelect: 'none' }}
      onTouchStart={handleTouchStart} onTouchEnd={handleTouchEnd}
      dir="rtl" data-testid="book-reader-page">

      {/* TOOLBAR */}
      <div className={`flex items-center justify-between px-3 md:px-6 py-2 md:py-3 shrink-0 transition-all duration-300 ${isFullscreen ? '-translate-y-full opacity-0 h-0 overflow-hidden' : ''}`}
        style={{ background: nightMode ? 'rgba(15,15,23,0.97)' : 'rgba(245,240,232,0.97)', zIndex: 50 }}>
        <div className="flex items-center gap-2 md:gap-4 min-w-0 flex-1">
          <button onClick={() => navigate(`/courses/${courseId}`)} className={`shrink-0 p-2 rounded-xl transition-colors ${btnClass(false)}`} data-testid="back-to-course-btn">
            <ArrowRight className="w-5 h-5 md:w-6 md:h-6" />
          </button>
          <h1 className={`text-sm md:text-lg font-bold truncate ${nightMode ? 'text-amber-100' : 'text-amber-900'}`}>{bookTitle}</h1>
        </div>
        <div className="flex items-center gap-1 md:gap-2 shrink-0 mx-2 md:mx-6">
          <button onClick={nextPage} disabled={currentPage >= totalPages} className={`p-1.5 md:p-2 rounded-lg transition-colors disabled:opacity-30 ${btnClass(false)}`} data-testid="next-page-btn">
            <ChevronRight className="w-4 h-4 md:w-5 md:h-5" />
          </button>
          <div className="flex items-center gap-1 md:gap-2">
            <input type="number" value={pageInputValue} onChange={e => setPageInputValue(e.target.value)}
              onKeyDown={handlePageInput} placeholder={String(currentPage)} min={1} max={totalPages}
              className={`w-12 md:w-16 text-center text-sm md:text-base font-bold rounded-lg py-1 px-1 border-0 outline-none ${nightMode ? 'bg-white/10 text-amber-200 placeholder:text-amber-300' : 'bg-black/5 text-amber-900 placeholder:text-amber-800'}`}
              data-testid="page-number-input" />
            <span className={`text-xs md:text-sm ${nightMode ? 'text-amber-300/60' : 'text-amber-700/60'}`}>/ {totalPages}</span>
          </div>
          <button onClick={prevPage} disabled={currentPage <= 1} className={`p-1.5 md:p-2 rounded-lg transition-colors disabled:opacity-30 ${btnClass(false)}`} data-testid="prev-page-btn">
            <ChevronLeft className="w-4 h-4 md:w-5 md:h-5" />
          </button>
        </div>
        <div className="flex items-center gap-0.5 md:gap-1.5 shrink-0">
          <button onClick={zoomOut} disabled={zoomIndex <= 0} className={`p-1.5 md:p-2 rounded-lg transition-colors disabled:opacity-30 ${btnClass(false)}`} data-testid="zoom-out-btn"><ZoomOut className="w-4 h-4 md:w-5 md:h-5" /></button>
          <button onClick={resetZoom} className={`hidden md:block px-2 py-1 rounded-lg text-xs font-bold transition-colors ${btnClass(false)}`} data-testid="zoom-reset-btn">{Math.round(scale * 100)}%</button>
          <button onClick={zoomIn} disabled={zoomIndex >= ZOOM_LEVELS.length - 1} className={`p-1.5 md:p-2 rounded-lg transition-colors disabled:opacity-30 ${btnClass(false)}`} data-testid="zoom-in-btn"><ZoomIn className="w-4 h-4 md:w-5 md:h-5" /></button>
          <div className={`hidden md:block w-px h-6 mx-1 ${nightMode ? 'bg-white/10' : 'bg-black/10'}`} />
          <button onClick={toggleBookmark} className={`p-1.5 md:p-2 rounded-lg transition-colors ${btnClass(isBookmarked)}`} data-testid="bookmark-btn">
            {isBookmarked ? <BookmarkCheck className="w-4 h-4 md:w-5 md:h-5" /> : <Bookmark className="w-4 h-4 md:w-5 md:h-5" />}
          </button>
          <button onClick={() => setShowBookmarks(v => !v)} className={`hidden md:block p-1.5 md:p-2 rounded-lg transition-colors relative ${btnClass(false)}`} data-testid="bookmarks-list-btn">
            <Bookmark className="w-4 h-4 md:w-5 md:h-5" />
            {bookmarks.length > 0 && <span className="absolute -top-0.5 -left-0.5 w-4 h-4 bg-amber-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">{bookmarks.length}</span>}
          </button>
          <div className="relative hidden md:block">
            <button onClick={() => setQuestionVisibility(v => v === 'all' ? 'mine' : v === 'mine' ? 'hidden' : 'all')}
              className={`p-1.5 md:p-2 rounded-lg transition-colors ${btnClass(questionVisibility !== 'hidden')}`}
              title={questionVisibility === 'all' ? 'جميع الأسئلة' : questionVisibility === 'mine' ? 'أسئلتي فقط' : 'مخفي'}
              data-testid="question-visibility-btn">
              {questionVisibility === 'hidden' ? <EyeOff className="w-4 h-4 md:w-5 md:h-5" /> : questionVisibility === 'mine' ? <User className="w-4 h-4 md:w-5 md:h-5" /> : <Eye className="w-4 h-4 md:w-5 md:h-5" />}
            </button>
            <span className={`absolute -bottom-5 left-1/2 -translate-x-1/2 text-[9px] whitespace-nowrap ${nightMode ? 'text-amber-400/60' : 'text-amber-700/60'}`}>
              {questionVisibility === 'all' ? 'الكل' : questionVisibility === 'mine' ? 'أسئلتي' : 'مخفي'}
            </span>
          </div>
          <button onClick={() => setShowQuestionsPanel(v => !v)} className={`p-1.5 md:p-2 rounded-lg transition-colors relative ${btnClass(false)}`} data-testid="questions-btn">
            <MessageCircleQuestion className="w-4 h-4 md:w-5 md:h-5" />
            {questionsCount > 0 && <span className="absolute -top-0.5 -left-0.5 w-4 h-4 bg-emerald-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center">{questionsCount}</span>}
          </button>
          <div className={`hidden md:block w-px h-6 mx-1 ${nightMode ? 'bg-white/10' : 'bg-black/10'}`} />
          {!fromQuestion && (
            <div className="flex items-center gap-1">
              <button
                onClick={() => setShowReportModal(true)}
                className={`p-1.5 md:p-2 rounded-lg transition-colors ${btnClass(false)} text-emerald-600 relative`}
                data-testid="send-report-btn"
                title={reportPagesCount > 0 ? `إرسال تقرير قرائي — ${reportPagesCount} صفحة جديدة` : 'إرسال التقرير القرائي'}
              >
                <FileText className="w-4 h-4 md:w-5 md:h-5" />
                {reportPagesCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-emerald-600 text-white text-[9px] font-bold rounded-full min-w-[16px] h-[16px] flex items-center justify-center px-1 shadow-md" data-testid="new-pages-badge">
                    +{reportPagesCount}
                  </span>
                )}
              </button>
              {lastSubmittedPage > 0 && (
                <div className={`hidden md:flex flex-col items-center justify-center leading-tight px-1.5 ${nightMode ? 'text-emerald-300' : 'text-emerald-700'}`} data-testid="report-progress-pill" title={`آخر تقرير: صفحة ${lastSubmittedPage} من ${totalPages}`}>
                  <span className="text-[10px] font-semibold">آخر تقرير</span>
                  <span className="text-[11px] font-bold tabular-nums">{lastSubmittedPage}/{totalPages}</span>
                </div>
              )}
            </div>
          )}
          {fromQuestion && (
            <div
              className={`hidden md:flex items-center gap-1.5 px-2 py-1 rounded-lg text-[11px] font-medium ${nightMode ? 'bg-amber-500/15 text-amber-300 border border-amber-500/25' : 'bg-amber-50 text-amber-800 border border-amber-200'}`}
              data-testid="viewing-from-question-badge"
              title="لا يظهر زر إرسال التقرير القرائي لأنك دخلت الكتاب من صفحة سؤال — رجاءً لا يُحسب ما لم تقرأه ضمن تقاريرك."
            >
              وضع المراجعة (سؤال)
            </div>
          )}
          <div className={`hidden md:block w-px h-6 mx-1 ${nightMode ? 'bg-white/10' : 'bg-black/10'}`} />
          <button onClick={() => setNightMode(m => !m)} className={`p-1.5 md:p-2 rounded-lg transition-colors ${btnClass(false)}`} data-testid="night-mode-btn">
            {nightMode ? <Sun className="w-4 h-4 md:w-5 md:h-5" /> : <Moon className="w-4 h-4 md:w-5 md:h-5" />}
          </button>
          <button onClick={toggleFullscreen} className={`hidden md:block p-1.5 md:p-2 rounded-lg transition-colors ${btnClass(false)}`} data-testid="fullscreen-btn">
            {isFullscreen ? <Minimize className="w-4 h-4 md:w-5 md:h-5" /> : <Maximize className="w-4 h-4 md:w-5 md:h-5" />}
          </button>
        </div>
      </div>

      {!isFullscreen && (
        <div className="w-full h-1 relative shrink-0" style={{ background: nightMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.05)' }}>
          <div className="h-full transition-all duration-500 rounded-l-full" style={{ width: `${progressPercent}%`, background: 'linear-gradient(90deg, #C19A6B, #D4AF37)' }} />
        </div>
      )}

      {/* MAIN CONTENT */}
      <div ref={containerRef} className="flex-1 overflow-auto flex justify-center items-start" style={{ background: nightMode ? '#0f0f17' : '#e8e0d4' }}>
        <div className="py-2 md:py-4 px-2 md:px-4 w-full flex justify-center">
          <div ref={pageContainerRef} className="relative shadow-2xl rounded-sm overflow-hidden cursor-crosshair"
            style={{ filter: nightMode ? 'invert(0.88) hue-rotate(180deg)' : 'none', maxWidth: '100%' }}
            onClick={handlePageClick}
            onTouchStart={handlePageTouchStart} onTouchEnd={handlePageTouchEnd}>
            {pageLoading && (
              <div className="absolute inset-0 flex items-center justify-center bg-white/60 z-10">
                <Loader2 className="w-10 h-10 text-amber-600 animate-spin" />
              </div>
            )}
            <img key={`${bookKey}-${currentPage}`} src={pageImageUrl(currentPage)} alt={`صفحة ${currentPage}`}
              className="block" draggable={false}
              style={{
                // Default (scale=1 / index=2): fit the page entirely inside the viewport — both width and height — so desktop users see the full page without scrolling. Mobile keeps full-width behavior because viewport height is generous relative to width.
                width: scale === 1 ? 'auto' : `${Math.round(scale * 100)}%`,
                maxWidth: '100%',
                maxHeight: scale === 1 ? 'calc(100vh - 140px)' : 'none',
                height: 'auto',
                pointerEvents: 'none',
              }}
              onLoadStart={() => setPageLoading(true)} onLoad={() => setPageLoading(false)} onError={() => setPageLoading(false)}
              data-testid="page-image" />

            {/* Question markers - green bars on right edge */}
            {questionVisibility !== 'hidden' && visibleQuestions.filter(q => q.text_anchor && (q.text_anchor.ny || q.text_anchor.y)).map(q => (
              <button key={q.id} className="absolute cursor-pointer group" style={{ right: 0, top: `${Math.max(1, Math.min(97, (q.text_anchor.ny || q.text_anchor.y) * 100))}%`, zIndex: 10, width: '8px', height: '40px', transform: 'translateY(-50%)' }}
                onClick={(e) => { e.stopPropagation(); setActiveQuestion(q); }} data-testid={`question-marker-${q.id}`}>
                <div className="w-full h-full rounded-l-sm transition-all group-hover:w-4 group-hover:shadow-lg"
                  style={{ background: q.status === 'answered' ? '#10b981' : '#f59e0b' }} />
              </button>
            ))}

            {/* Click anchor marker */}
            {clickAnchor && (
              <div className="absolute z-20 pointer-events-none" style={{ left: `${clickAnchor.nx * 100}%`, top: `${clickAnchor.ny * 100}%`, transform: 'translate(-50%, -50%)' }}>
                <div className="w-6 h-6 rounded-full border-2 border-amber-500 bg-amber-500/20 animate-pulse" />
              </div>
            )}

            {/* Ask button at click anchor */}
            {clickAnchor && (
              <div className="absolute z-20" style={{ left: `${clickAnchor.nx * 100}%`, top: `${clickAnchor.ny * 100}%`, transform: 'translate(-50%, calc(-100% - 20px))' }}>
                <div className="flex gap-1">
                  <button onClick={(e) => { e.stopPropagation(); setShowAskModal(true); }}
                    className="flex items-center gap-1.5 px-3 py-2 rounded-xl shadow-xl text-sm font-bold text-white whitespace-nowrap"
                    style={{ background: 'linear-gradient(135deg, #C19A6B, #8B6F47)' }} data-testid="ask-about-btn">
                    <MessageCircleQuestion className="w-4 h-4" /> استفسر عن هذا الموضع
                  </button>
                  <button onClick={(e) => { e.stopPropagation(); setClickAnchor(null); }}
                    className="p-2 rounded-xl shadow-xl bg-gray-600 text-white hover:bg-gray-700">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* MOBILE BOTTOM BAR */}
      {!isFullscreen && (
        <div className="md:hidden flex items-center justify-between px-4 py-2 shrink-0"
          style={{ background: nightMode ? 'rgba(15,15,23,0.97)' : 'rgba(245,240,232,0.97)' }}>
          <button onClick={nextPage} disabled={currentPage >= totalPages}
            className={`flex items-center gap-1 px-3 py-2 rounded-xl text-sm font-bold transition-colors disabled:opacity-30 ${nightMode ? 'bg-amber-400/20 text-amber-300' : 'bg-amber-800/10 text-amber-800'}`}>
            <ChevronRight className="w-4 h-4" /> التالي
          </button>
          <div className="flex items-center gap-2">
            <button onClick={toggleBookmark} className={isBookmarked ? 'text-amber-500' : nightMode ? 'text-amber-300' : 'text-amber-800'}>
              {isBookmarked ? <BookmarkCheck className="w-5 h-5" /> : <Bookmark className="w-5 h-5" />}
            </button>
            <button onClick={() => setQuestionVisibility(v => v === 'all' ? 'mine' : v === 'mine' ? 'hidden' : 'all')}
              className={questionVisibility !== 'hidden' ? 'text-emerald-600' : nightMode ? 'text-amber-300/50' : 'text-amber-800/50'}>
              {questionVisibility === 'hidden' ? <EyeOff className="w-5 h-5" /> : questionVisibility === 'mine' ? <User className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
            <button onClick={() => setShowQuestionsPanel(v => !v)} className={`relative ${nightMode ? 'text-amber-300' : 'text-amber-800'}`}>
              <MessageCircleQuestion className="w-5 h-5" />
              {questionsCount > 0 && <span className="absolute -top-1 -left-1 w-3.5 h-3.5 bg-emerald-500 text-white text-[8px] font-bold rounded-full flex items-center justify-center">{questionsCount}</span>}
            </button>
            <span className={`text-sm font-bold ${nightMode ? 'text-amber-200/80' : 'text-amber-800/80'}`}>{currentPage} / {totalPages}</span>
          </div>
          <button onClick={prevPage} disabled={currentPage <= 1}
            className={`flex items-center gap-1 px-3 py-2 rounded-xl text-sm font-bold transition-colors disabled:opacity-30 ${nightMode ? 'bg-amber-400/20 text-amber-300' : 'bg-amber-800/10 text-amber-800'}`}>
            السابق <ChevronLeft className="w-4 h-4" />
          </button>
        </div>
      )}

      {isFullscreen && (
        <button onClick={toggleFullscreen} className="fixed top-4 left-4 z-[60] p-2 rounded-lg bg-black/50 text-white/70 hover:bg-black/80 transition-colors" data-testid="exit-fullscreen-btn">
          <Minimize className="w-5 h-5" />
        </button>
      )}

      {/* ASK QUESTION MODAL */}
      {showAskModal && (
        <div className="fixed inset-0 z-[300] flex items-center justify-center" onClick={() => setShowAskModal(false)}>
          <div className="absolute inset-0 bg-black/40" />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden" onClick={e => e.stopPropagation()} dir="rtl" data-testid="ask-question-modal">
            <div className="flex items-center justify-between p-4 border-b border-gray-100" style={{ background: 'linear-gradient(135deg, #fef3c7, #fde68a)' }}>
              <h3 className="text-lg font-bold text-amber-900">استفسر عن هذا الموضع</h3>
              <button onClick={() => setShowAskModal(false)} className="p-1 rounded-lg hover:bg-amber-200/50"><X className="w-5 h-5 text-amber-800" /></button>
            </div>
            <div className="p-4 space-y-4">
              <div className="bg-amber-50 rounded-xl p-3 text-sm">
                <span className="font-bold text-amber-900">{bookTitle}</span>
                <span className="text-amber-700 mr-1">- صفحة {currentPage}</span>
              </div>
              <div>
                <label className="text-xs text-gray-600 mb-1 block">سؤالك:</label>
                <textarea value={questionText} onChange={e => setQuestionText(e.target.value)}
                  placeholder="اكتب سؤالك أو استفسارك هنا..."
                  className="w-full h-28 border border-gray-200 rounded-xl p-3 text-sm resize-none focus:outline-none focus:border-amber-400 focus:ring-2 focus:ring-amber-100"
                  dir="rtl" data-testid="question-input" />
              </div>
              <button onClick={submitQuestion} disabled={submittingQ}
                className="w-full py-3 rounded-xl font-bold text-white transition-colors flex items-center justify-center gap-2 disabled:opacity-50"
                style={{ background: 'linear-gradient(135deg, #C19A6B, #8B6F47)' }} data-testid="submit-question-btn">
                {submittingQ ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
                {submittingQ ? 'جارٍ الإرسال...' : 'إرسال السؤال'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Q&A popup */}
      {activeQuestion && (
        <div className="fixed inset-0 z-[300] flex items-center justify-center" onClick={() => setActiveQuestion(null)}>
          <div className="absolute inset-0 bg-black/40" />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden" onClick={e => e.stopPropagation()} dir="rtl" data-testid="question-answer-modal">
            <div className="p-4 border-b border-gray-100" style={{ background: 'linear-gradient(135deg, #d1fae5, #a7f3d0)' }}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2"><CheckCircle className="w-5 h-5 text-emerald-700" /><h3 className="text-lg font-bold text-emerald-900">سؤال وجواب</h3></div>
                <button onClick={() => setActiveQuestion(null)} className="p-1 rounded-lg hover:bg-emerald-200/50"><X className="w-5 h-5 text-emerald-800" /></button>
              </div>
              <p className="text-xs text-emerald-700 mt-1">صفحة {activeQuestion.page_num}</p>
            </div>
            <div className="p-4 space-y-4 max-h-[60vh] overflow-y-auto">
              {activeQuestion.selected_text && (
                <div className="bg-amber-50 rounded-xl p-3 border-r-4 border-amber-400">
                  <p className="text-xs text-amber-600 mb-1">النص المحدد:</p>
                  <p className="text-sm text-amber-900">{activeQuestion.selected_text}</p>
                </div>
              )}
              <div className="bg-blue-50 rounded-xl p-3 border-r-4 border-blue-400">
                <p className="text-xs text-blue-600 mb-1">السؤال:</p>
                <p className="text-sm text-blue-900 font-medium">{activeQuestion.question}</p>
                <p className="text-xs text-blue-500 mt-2">سؤال أحد القراء</p>
              </div>
              {activeQuestion.answer ? (
                <div className="bg-emerald-50/40 rounded-xl p-3 border-r-4 border-emerald-300">
                  <p className="text-xs text-emerald-600 mb-1">الإجابة:</p>
                  <div
                    className="text-sm text-emerald-900 leading-relaxed answer-html prose prose-sm max-w-none"
                    dir="rtl"
                    dangerouslySetInnerHTML={{ __html: activeQuestion.answer }}
                  />
                  {Array.isArray(activeQuestion.answer_refs) && activeQuestion.answer_refs.length > 0 && (
                    <div className="mt-3 pt-3 border-t-2 border-emerald-200">
                      <p className="text-xs font-bold text-emerald-700 mb-1.5">المراجع:</p>
                      <ol className="text-xs text-emerald-900 list-decimal pr-5 space-y-1" dir="rtl">
                        {activeQuestion.answer_refs.map((r, i) => (
                          <li key={i} id={`fn-${i + 1}`}>{r}</li>
                        ))}
                      </ol>
                    </div>
                  )}
                </div>
              ) : (
                <div className="bg-yellow-50 rounded-xl p-3 text-sm text-yellow-800">سؤالك قيد المراجعة، سيتم إشعارك عند الإجابة.</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Questions Panel */}
      {showQuestionsPanel && (
        <div className="fixed inset-0 z-[300] flex items-center justify-center" onClick={() => setShowQuestionsPanel(false)}>
          <div className="absolute inset-0 bg-black/30" />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-4 max-h-[70vh] overflow-hidden" onClick={e => e.stopPropagation()} dir="rtl" data-testid="questions-panel">
            <div className="flex items-center justify-between p-4 border-b border-gray-100">
              <h3 className="text-lg font-bold text-amber-900">أسئلة الصفحة {currentPage}</h3>
              <button onClick={() => setShowQuestionsPanel(false)} className="p-1 rounded-lg hover:bg-gray-100"><X className="w-5 h-5 text-gray-500" /></button>
            </div>
            <div className="overflow-y-auto max-h-[55vh]">
              {pageQuestions.length > 0 && (
                <div className="p-2 space-y-1">
                  <p className="text-xs text-emerald-600 font-bold px-2 pt-2">أسئلة مجابة</p>
                  {pageQuestions.map(q => (
                    <button key={q.id} onClick={() => { setActiveQuestion(q); setShowQuestionsPanel(false); }}
                      className="w-full text-right p-3 rounded-xl hover:bg-emerald-50 transition-colors border border-gray-100">
                      <p className="text-sm font-medium text-gray-900 line-clamp-2">{q.question}</p>
                      {q.selected_text && <p className="text-xs text-gray-500 mt-1 line-clamp-1">"{q.selected_text}"</p>}
                    </button>
                  ))}
                </div>
              )}
              {myPageQuestions.filter(q => q.status !== 'answered').length > 0 && (
                <div className="p-2 space-y-1">
                  <p className="text-xs text-yellow-600 font-bold px-2 pt-2">أسئلتي (قيد المراجعة)</p>
                  {myPageQuestions.filter(q => q.status !== 'answered').map(q => (
                    <button key={q.id} onClick={() => { setActiveQuestion(q); setShowQuestionsPanel(false); }}
                      className="w-full text-right p-3 rounded-xl hover:bg-yellow-50 transition-colors border border-gray-100">
                      <p className="text-sm font-medium text-gray-900 line-clamp-2">{q.question}</p>
                      <span className="text-xs text-yellow-600 mt-1">قيد المراجعة</span>
                    </button>
                  ))}
                </div>
              )}
              {pageQuestions.length === 0 && myPageQuestions.length === 0 && (
                <div className="p-8 text-center">
                  <MessageCircleQuestion className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500 text-sm">لا توجد أسئلة في هذه الصفحة</p>
                  <p className="text-gray-400 text-xs mt-1">انقر على موضع في الصفحة لطرح سؤال</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Bookmarks Panel */}
      {showBookmarks && (
        <div className="fixed inset-0 z-[300] flex items-center justify-center" onClick={() => setShowBookmarks(false)}>
          <div className="absolute inset-0 bg-black/30" />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-sm mx-4 max-h-[70vh] overflow-hidden" onClick={e => e.stopPropagation()} dir="rtl" data-testid="bookmarks-panel">
            <div className="flex items-center justify-between p-4 border-b border-gray-100">
              <h3 className="text-lg font-bold text-amber-900">الإشارات المرجعية</h3>
              <button onClick={() => setShowBookmarks(false)} className="p-1 rounded-lg hover:bg-gray-100"><X className="w-5 h-5 text-gray-500" /></button>
            </div>
            <div className="overflow-y-auto max-h-[55vh]">
              {bookmarks.length === 0 ? (
                <div className="p-8 text-center"><Bookmark className="w-12 h-12 text-gray-300 mx-auto mb-3" /><p className="text-gray-500 text-sm">لا توجد إشارات مرجعية</p></div>
              ) : (
                <div className="p-2">{bookmarks.map(page => (
                  <button key={page} onClick={() => { goToPage(page); setShowBookmarks(false); }}
                    className="w-full flex items-center gap-3 p-3 rounded-xl hover:bg-amber-50 transition-colors text-right">
                    <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center shrink-0"><BookmarkCheck className="w-5 h-5 text-amber-700" /></div>
                    <span className="font-bold text-amber-900">صفحة {page}</span>
                    <span className="text-xs text-gray-500">من {totalPages}</span>
                  </button>
                ))}</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Reading Report Confirmation Modal */}
      {showReportModal && (
        <div className="fixed inset-0 z-[400] flex items-center justify-center" onClick={() => !submittingReport && setShowReportModal(false)}>
          <div className="absolute inset-0 bg-black/50" />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 overflow-hidden" onClick={e => e.stopPropagation()} dir="rtl" data-testid="reading-report-modal">
            <div className="bg-gradient-to-l from-emerald-600 to-emerald-700 px-5 py-4 flex items-center justify-between">
              <div className="flex items-center gap-2 text-white">
                <FileText className="w-6 h-6" />
                <h3 className="text-lg font-bold">تأكيد إرسال التقرير القرائي</h3>
              </div>
              <button onClick={() => setShowReportModal(false)} disabled={submittingReport} className="p-1 rounded-lg hover:bg-white/20 text-white">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">الكتاب:</span>
                  <span className="font-bold text-amber-900">{bookTitle}</span>
                </div>
                {bookAuthor && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600">المؤلف:</span>
                    <span className="font-bold text-amber-900">{bookAuthor}</span>
                  </div>
                )}
                <div className="flex justify-between text-sm border-t border-amber-200 pt-2">
                  <span className="text-gray-600">من صفحة:</span>
                  <span className="font-bold text-emerald-700">{reportFromPage}</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">إلى صفحة:</span>
                  <span className="font-bold text-emerald-700">{currentPage}</span>
                </div>
                <div className="flex justify-between text-sm border-t border-amber-200 pt-2">
                  <span className="text-gray-700 font-semibold">عدد الصفحات:</span>
                  <span className="font-bold text-emerald-800 text-base">{reportPagesCount} صفحة</span>
                </div>
                {currentPage >= totalPages && (
                  <div className="mt-2 pt-2 border-t border-amber-200 text-center">
                    <span className="inline-flex items-center gap-1 text-green-700 font-bold text-sm">
                      <CheckCircle className="w-4 h-4" /> ختمت الكتاب — ما شاء الله!
                    </span>
                  </div>
                )}
              </div>
              {reportPagesCount <= 0 ? (
                <div className="bg-gray-50 border border-gray-200 rounded-xl p-3 text-sm text-gray-600 text-center">
                  لا توجد صفحات جديدة لإرسالها — أنت بالفعل عند آخر صفحة أُرسل عنها تقرير.
                </div>
              ) : (
                <p className="text-xs text-gray-500 text-center">سيُرسَل التقرير تلقائياً إلى الإدارة.</p>
              )}
              <div className="flex gap-3">
                <button onClick={() => setShowReportModal(false)} disabled={submittingReport}
                  className="flex-1 py-3 rounded-xl border-2 border-gray-200 text-gray-700 font-semibold hover:bg-gray-50 transition-colors">
                  إلغاء
                </button>
                <button onClick={submitReadingReport} disabled={submittingReport || reportPagesCount <= 0}
                  className="flex-1 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                  data-testid="confirm-report-btn">
                  {submittingReport ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                  متأكد — إرسال
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>{`
        [data-testid="book-reader-page"] img { -webkit-touch-callout: none; pointer-events: none; }
        @media print { [data-testid="book-reader-page"] { display: none !important; } }
      `}</style>
    </div>
  );
};

export default BookReaderPage;
